
import { defineComponent, onBeforeUnmount, watch } from 'vue';
import _debounce from 'lodash/debounce';
import _uniqueId from 'lodash/uniqueId';
import PIS from '@/common/api/PIS';
import { ComponentName } from '@/catalogs/api/configuration/components/ComponentName';
import { IOptions } from '../api/runtime/IOptions';
import { DisplayMode, IRouteData } from '../api/runtime/IRouteData';
import { Instance } from '../api/Instance';

const INSTANCE_NAME_PREFIX = 'pisCatalogsInstance_';

export default defineComponent({
  name: ComponentName.instance,

  props: {
    // Allow to configure the APP before displaying it
    // Without this property developers will need to use `instance.init({})` on their own.
    // We use `String` type because adding empty `init` attribute in react apps converts it into `init="true"`.
    init: [Boolean, String],
    instanceId: String,
    baseUrl: String,
    accessToken: String,

    styles: String,

    settingAppCode: String,
    settingCatalogType: String,
    settingLangCode: String,

    exportPricesEnabled: Boolean,
    exportPricesAgreementId: String,
    exportPricesCustomerId: String,

    evDisplayMode: String,
    evDisplayModes: String,
    // Component specific
    dashboard: Boolean,
    inheritStyles: [Boolean, String],
  },

  setup(props) {
    const instance = PIS.Catalogs.getInstance(
      props.instanceId ?? _uniqueId(INSTANCE_NAME_PREFIX),
    ) as Instance;

    const getInstanceId = () => instance?.id;
    const getInstance = () => instance;
    const requiredPropNames = ['accessToken', 'settingAppCode'];

    const handlePropertyChanged = () => {
      if (instance?.isInitialized()) {
        // TODO: we need to update props, app settings will be watched

        if (props.accessToken) {
          instance.store.options.accessToken = props.accessToken;
        }
        const appOptions = instance.store.options.application;

        if (props.settingLangCode && appOptions) {
          appOptions.langCode = props.settingLangCode;
        }

        return;
      }

      if (props.dashboard) {
        instance.props['dashboard'] = true;
      }

      const displayModesSet = toArray(props.evDisplayModes);
      const displayModesAllowed = Object.values(DisplayMode);
      const displayModes =
        displayModesSet && displayModesSet.length
          ? displayModesAllowed.filter((mode) => displayModesSet.includes(mode))
          : displayModesAllowed;

      const settings = {
        baseUrl: props.baseUrl,
        accessToken: props.accessToken,
        application: {
          appCode: props.settingAppCode,
          langCode: props.settingLangCode,
          catalogType: props.settingCatalogType,
        },
        components: {
          productsSearchToolbar: {
            displayModes,
          },
          catalogsExport: {
            visible: true,
            exportPrices: props.exportPricesEnabled,
            omsAgreementId: props.exportPricesAgreementId,
            omsCustomerId: props.exportPricesCustomerId,
          },
        },
        styles: props.styles ? JSON.parse(props.styles) : undefined,
        inheritStyles: !!props.inheritStyles,
      } as IOptions;

      const route = {
        view: 'catalogs',
        catalogs: {},
        products: {
          displayMode: props.evDisplayMode ? DisplayMode[props.evDisplayMode] : displayModes[0],
        },
      } as IRouteData;

      (instance as any)['_routeJson'] = JSON.stringify(route, null, 4);
      (instance as any)['_optionsJson'] = JSON.stringify(settings, null, 4);

      if (props.init) {
        const missingProps = requiredPropNames.filter((propName) => !(props as any)[propName]);

        if (missingProps?.length) {
          instance?.logger.log(`Following required props are missing: ${missingProps.join(', ')}`);
        } else {
          instance?.init(settings, route);
        }
      } else {
        instance?.update(settings, route);
      }
    };

    const toArray = (val: string | undefined): string[] | undefined => {
      if (val === undefined) return undefined;

      return val.split(',').map((chunk) => chunk?.trim());
    };

    onBeforeUnmount(() => {
      instance?.destroy();
    });

    watch(props, _debounce(handlePropertyChanged, 50), { immediate: true });

    return {
      getInstanceId,
      getInstance,
    };
  },
});
